import React, { useEffect, useState, useContext, useRef } from 'react';
import { useLocation } from 'react-router-dom';

import {
  Container,
  Typography,
  Card,
  CardContent,
  CircularProgress,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Stack,
  Tooltip,
  Chip,
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Divider
} from '@mui/material';

import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import interactionPlugin from '@fullcalendar/interaction';
import timeGridPlugin from '@fullcalendar/timegrid';
import listPlugin from '@fullcalendar/list';
import itLocale from '@fullcalendar/core/locales/it';

import scadenzaApi from '../../api/scadenzaApi';
import contribuenteApi from '../../api/contribuenteApi';
import { AuthContext } from '../../auth/AuthContext';
import { formatDate } from '../../utils/dateFormatter';
import PaymentIcon from '@mui/icons-material/Payment';
import EventIcon from '@mui/icons-material/Event';
import { scadenzaStatusMap } from '../../utils/scadenzaStatusMapper';
import QRCode from 'react-qr-code';
import { QrCode2 } from '@mui/icons-material';

import '../../styles/calendar.css';

export default function ScadenzeCalendarPageDesktop() {

  const { user, token } = useContext(AuthContext);

  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const [filtroStato, setFiltroStato] = useState('da_pagare');
  const [filtroContribuente, setFiltroContribuente] =
    useState(user.role === 'CONTRIBUENTE' ? user.contribuenteId : null);

  const [contribuenti, setContribuenti] = useState([]);
  const [scadenzeMese, setScadenzeMese] = useState(0);

  const [modalOpen, setModalOpen] = useState(false);
  const [selectedScad, setSelectedScad] = useState(null);

  const today = new Date();
  const [calendarMonth] = useState(today.getMonth());
  const [calendarYear] = useState(today.getFullYear());

  const location = useLocation();
  const query = new URLSearchParams(location.search);
  const scadenzaHighlight = query.get("scadenza");

  const calendarRef = useRef(null);

  const statoColors = {
    da_pagare: 'red',
    pagata: 'green',
    scaduta: 'yellow'
  };

  /* ================= INIT ================= */

  useEffect(() => {
    fetchContribuenti();
  }, []);

  useEffect(() => {
    fetchScadenze();
  }, [filtroStato, filtroContribuente]);

  const fetchContribuenti = async () => {
    if (user.role === "STUDIO") {
      const params = { page: 0, size: 100000, sort: "cognome,ASC" };
      const r = await contribuenteApi.listbystudio(user.studioId, params);
      setContribuenti(r.data.content || []);
    }
  };

  /* ================= FETCH ================= */

  const fetchScadenze = async () => {

    if (user.role === "STUDIO" && !filtroContribuente) return;
    if (!token) return;

    try {
      setLoading(true);

      let res;

      if (filtroStato === "tutte") {
        res = await scadenzaApi.listbycontribuenteandstato(filtroContribuente, { stato: '' });
      } else {
        res = await scadenzaApi.listbycontribuenteandstato(filtroContribuente, { stato: filtroStato });
      }

      const data = res.data || [];
      setScadenzeMese(data.length);

      const fcEvents = data.map(s => ({
        id: s.id.toString(),
        title: s.tipo,
        start: s.dataScadenza,
        backgroundColor: s.id.toString() === scadenzaHighlight ? '#ff9800' : statoColors[s.stato] || '#1976d2',
        borderColor: s.id.toString() === scadenzaHighlight ? '#000000' : statoColors[s.stato] || '#1976d2',
        extendedProps: { ...s }
      }));

      setEvents(fcEvents);

      if (scadenzaHighlight) {
        const api = calendarRef.current.getApi();
        const target = data.find(s => s.id.toString() === scadenzaHighlight);

        if (target) {
          requestAnimationFrame(() => {
            api.changeView('timeGridWeek', target.dataScadenza);

            const el = document.querySelector(`[data-event-id="${target.id}"]`);
            if (el) {
              el.classList.add('blink-event');
              setTimeout(() => el.classList.remove('blink-event'), 2000);
            }
          });
        }
      }

    } catch (err) {
      console.error(err);
      setError("Errore caricamento scadenze");
    } finally {
      setLoading(false);
    }
  };

  /* ================= ACTION ================= */

  const markPaid = async (id) => {
    await scadenzaApi.setpagatamanuale(id);
    fetchScadenze();
  };

  const onEventClick = (info) => {
    setSelectedScad(info.event.extendedProps);
    setModalOpen(true);
  };

  const handleStatoChange = e => setFiltroStato(e.target.value);
  const handleContribuenteChange = e => setFiltroContribuente(e.target.value);

  /* ================= EVENT UI ================= */

  const renderEventContent = (eventInfo) => {
    const s = eventInfo.event.extendedProps;
    const cfg = scadenzaStatusMap[s.direzione!="ENTRATA"?s.stato:s.stato!="pagata"?"da_incassare":s.stato];

    return (
      <Tooltip
        title={
          <Box>
            <Typography variant="body2"><b>{s.tipo}</b></Typography>
            <Typography variant="caption">Rata: {s.numerorata}</Typography><br />
            <Typography variant="caption">Importo: {s.importo}€</Typography><br />
            <Typography variant="caption">Data: {formatDate(s.dataScadenza)}</Typography><br />
            <Typography variant="caption">Codice: {s.codiceModuloPagamento || '-'}</Typography>
          </Box>
        }
      >
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
              <Typography variant="caption" sx={{ fontWeight: 600 }}>
                {s.tipo}
              </Typography>

              <Typography variant="caption">
                {s.importo}€
              </Typography>

              <Box sx={{ mt: 0.5 }}>
                <Chip
                  label={cfg?.label || s.stato}
                  size="small"
                  color={cfg?.color || 'default'}
                  sx={{ fontSize: 10 }}
                />
              </Box>
            </Box>

      </Tooltip>
    );
  };

  /* ================= RENDER ================= */

  return (
    <Container sx={{ mt: 4, mb: 5 }}>

      {/* LOADING */}
      {loading && (
        <Box sx={{
          position: 'fixed',
          inset: 0,
          bgcolor: 'rgba(255,255,255,0.7)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1300
        }}>
          <CircularProgress size={48} />
        </Box>
      )}

      {error && <Alert severity="error">{error}</Alert>}

      {/* HEADER */}
      <Stack spacing={2} sx={{ mb: 3 }}>
        <Typography variant="h4" fontWeight={600}>
          Calendario Scadenze
        </Typography>

        <Chip
          icon={<EventIcon />}
          label={`Totale scadenze: ${scadenzeMese}`}
          color="primary"
          sx={{ width: 'fit-content' }}
        />
      </Stack>

      {/* FILTRI */}
      <Card sx={{ mb: 3 }}>
        <CardContent>

          <Typography variant="subtitle1" sx={{ mb: 2 }}>
            Filtri
          </Typography>

          <Stack direction="row" spacing={2} flexWrap="wrap">

            <FormControl sx={{ minWidth: 160 }}>
              <InputLabel>Stato</InputLabel>
              <Select value={filtroStato} label="Stato" onChange={handleStatoChange}>
                <MenuItem value="da_pagare">Da pagare</MenuItem>
                <MenuItem value="da_incassare">Da incassare</MenuItem>
                <MenuItem value="pagata">Pagata/Incassata</MenuItem>
                <MenuItem value="scaduta">Scaduta</MenuItem>
                <MenuItem value="tutte">Tutte</MenuItem>
              </Select>
            </FormControl>

            {contribuenti.length > 0 && (
              <FormControl sx={{ minWidth: 200 }}>
                <InputLabel>Contribuente</InputLabel>
                <Select
                  value={filtroContribuente}
                  label="Contribuente"
                  onChange={handleContribuenteChange}
                >
                  {contribuenti.map(c => (
                    <MenuItem key={c.id} value={c.id}>
                      {c.nome} {c.cognome}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}

          </Stack>

        </CardContent>
      </Card>

      {/* CALENDARIO */}
      <Card>
        <CardContent>

          <FullCalendar
            ref={calendarRef}
            plugins={[dayGridPlugin, timeGridPlugin, listPlugin, interactionPlugin]}
            initialView="listWeek"
            events={events}
            eventContent={renderEventContent}
            eventClick={onEventClick}
            locale={itLocale}
            headerToolbar={{
              left: 'prev,next today',
              center: 'title',
              right: ''
            }}
            height="auto"
            showNonCurrentDates={false}
          />

        </CardContent>
      </Card>

      {/* MODALE */}
      <Dialog open={modalOpen} onClose={() => setModalOpen(false)} fullWidth maxWidth="sm">

        <DialogTitle>Dettaglio Scadenza</DialogTitle>

        {selectedScad && (
          <DialogContent>

            <Stack spacing={1}>
              <Typography><b>Tipo:</b> {selectedScad.tipo}</Typography>
              <Typography><b>Rata:</b> {selectedScad.numerorata}</Typography>
              <Typography><b>Data:</b> {formatDate(selectedScad.dataScadenza)}</Typography>
              <Typography><b>Importo:</b> {selectedScad.importo} €</Typography>
              <Typography><b>Stato:</b> {selectedScad.stato}</Typography>
              <Typography><b>Codice:</b> {selectedScad.codiceModuloPagamento || '-'}</Typography>
                  {selectedScad.qrcode && selectedScad.qrcode!=="" && (
                  <Typography mt={1}>
                    <QRCode value={selectedScad.qrcode} />
                    {selectedScad.qrcode}
                  </Typography>
                  )}
            </Stack>

          </DialogContent>
        )}

        <Divider />

        <DialogActions>

          {user.role === 'CONTRIBUENTE'
            && selectedScad
            && selectedScad.stato !== "pagata" && (
              <Button
                variant="contained"
                color="success"
                startIcon={<PaymentIcon />}
                onClick={() => {
                  markPaid(selectedScad.id);
                  setModalOpen(false);
                }}
              >
                {selectedScad.direzione=="ENTRATA"?"Marca come incassata":"Marca come pagata"}
              </Button>
            )}

          <Button onClick={() => setModalOpen(false)}>
            Chiudi
          </Button>

        </DialogActions>

      </Dialog>

    </Container>
  );
}
